/**
 * How long a screen transition takes, and how that budget is divided.
 *
 * Everything about a transition's timing derives from the two numbers below —
 * every card's delay, every card's duration, the rail's travel, the moment the
 * route is swapped. Change `major` to 1100 and the whole thing re-times itself
 * with no other edit, which is the point: the right length is a thing you find
 * by feel on a handset, not a thing you reason out.
 *
 * `?tms=1100` in the URL overrides the major budget (and scales the minor with
 * it) for exactly that reason, `?tcard=150` overrides how big a card is drawn,
 * and `?transition=spread` picks the style — the same trick `perf=1`, `netdbg=1`
 * and `lp=1` already use, for the same reason: the question can only be answered
 * on the device, and a rebuild per guess is how you stop asking it. See
 * docs/LOCAL_DEV.md.
 */

/** The two lengths. A minor move between menus; a major one on or off a table. */
export type TransitionKind = "minor" | "major";

/**
 * How a transition looks. `fade` is not a card treatment — it is what a player
 * who has asked for reduced motion gets, and what a browser with no Web
 * Animations API falls back to.
 */
export type TransitionStyle = "slide" | "deal" | "spread" | "cut" | "fade";

/** The whole budget for each kind, start to finish, in milliseconds. */
export const TRANSITION_MS: Record<TransitionKind, number> = {
  minor: 1000,
  major: 1500,
};

/**
 * How each budget is split.
 *
 * `hold` is small but not optional: it is the beat where the screen is fully
 * covered and the route is swapped, and without a frame or two of it the swap
 * can land on the same frame as the first card starting to leave.
 *
 * The three must sum to 1. `transitionPlan` does not trust them to — it rounds
 * the first two and gives the remainder to `reveal`, so the parts always add up
 * to the budget exactly whatever these are set to.
 */
export const TRANSITION_SPLIT = { cover: 0.5, hold: 0.08, reveal: 0.42 };

/** Styles whose behavior is not "cover, swap, uncover" get their own division. */
const STYLE_SPLIT: Partial<Record<TransitionStyle, typeof TRANSITION_SPLIT>> = {
  // The slide has no covered beat at all. Nothing is covering anything: the old
  // screen leaves and the new one arrives, and the swap happens at the instant
  // both are travelling at their fastest. A hold there would be a pause on an
  // empty tray in the middle of one continuous movement.
  slide: { cover: 0.5, hold: 0, reveal: 0.5 },
};

/** What reduced motion gets instead: a plain, short cross-fade. */
export const REDUCED_MOTION_MS = 120;

/** The hard stop. See `transitionStore`: an overlay that never comes down is a blank app. */
export const TRANSITION_WATCHDOG_SLACK_MS = 400;

export interface TransitionPlan {
  kind: TransitionKind;
  style: TransitionStyle;
  /** Start to finish. `coverMs + holdMs + revealMs`, exactly. */
  totalMs: number;
  /** Cards are flying in. Every card has landed the instant this ends. */
  coverMs: number;
  /** Fully covered. The route is swapped at the start of this. */
  holdMs: number;
  /** Cards are leaving. Every card is gone the instant this ends. */
  revealMs: number;
}

/** The budget for a kind, honouring a `?tms=` override. */
export function budgetFor(kind: TransitionKind): number {
  const override = overriddenMajorMs();
  if (override === null) return TRANSITION_MS[kind];
  // The minor keeps its proportion of the major rather than being overridden
  // separately: one number to turn is the whole point of the flag.
  const ratio = TRANSITION_MS.minor / TRANSITION_MS.major;
  return kind === "major" ? override : Math.round(override * ratio);
}

/**
 * Divides a budget into its three phases.
 *
 * `revealMs` takes the remainder rather than being rounded on its own, so
 * `cover + hold + reveal === totalMs` is true for every budget rather than for
 * the ones that happen to divide evenly. Card timings are built against these
 * numbers, so a one-millisecond drift here is a card still moving when the
 * overlay is removed.
 */
export function transitionPlan(kind: TransitionKind, style: TransitionStyle, totalMs = budgetFor(kind)): TransitionPlan {
  if (style === "fade") {
    // Nothing covers anything, so the whole budget is one cross-fade and the
    // route is swapped in the middle of it.
    const half = Math.round(totalMs / 2);
    return { kind, style, totalMs, coverMs: half, holdMs: 0, revealMs: totalMs - half };
  }
  const split = STYLE_SPLIT[style] ?? TRANSITION_SPLIT;
  const coverMs = Math.round(totalMs * split.cover);
  const holdMs = Math.round(totalMs * split.hold);
  return { kind, style, totalMs, coverMs, holdMs, revealMs: totalMs - coverMs - holdMs };
}

/**
 * Routes that are a table, or the score pad a table hands over to.
 *
 * These get the major treatment because they are the moments worth dressing: a
 * round being dealt, and a round being scored. Everything else in this app is a
 * menu, and a menu that takes a second and a half to arrive is a slow app.
 *
 * Matched as prefixes because three of them carry an id.
 */
const MAJOR_ROUTE_PREFIXES = [
  "/local/play",
  "/local/leaderboard",
  "/online/play",
  "/online/watch",
  "/online/leaderboard",
  "/tutorial",
  "/campaign/play",
];

/**
 * The screens with a live table on them.
 *
 * Leaving one is a major move as surely as arriving at one, and not only for
 * symmetry. These tear down as they go — a table that has been left has no
 * round to draw — and a slide would carry that empty screen off in full view.
 * The cards cover it. (Local play also waits for the unmount to clear the
 * round, because the deal fills from the corners and the middle shows last.)
 */
const PLAY_ROUTE_PREFIXES = ["/local/play", "/online/play", "/tutorial", "/campaign/play"];

export interface TransitionRequest {
  /**
   * A turn clock is running at the destination.
   *
   * This DOWNGRADES a major to a minor, and it is not a nicety. The deal has
   * already happened by the time anything navigates — a local table's clock
   * starts the moment the play screen mounts, under the cover, and online the server's deal
   * is ticking before the lobby hands over — so a second and a half of cover is
   * a second and a half off somebody's first turn. Most tables have no clock at
   * all (`turnTimerSeconds` defaults to null), which is why this is a downgrade
   * rather than a reason to drop the whole idea.
   */
  hasTurnClock?: boolean;
  /** A retreat rather than an advance: the Cut travels the other way. */
  back?: boolean;
  /** Force a kind, for a call site that knows better than the route does. */
  kind?: TransitionKind;
}

/** Which length a navigation gets. `from` is where it is leaving, which matters on the way out of a table. */
export function transitionKindFor(path: string, req: TransitionRequest & { from?: string } = {}): TransitionKind {
  if (req.kind) return req.kind;
  if (req.hasTurnClock) return "minor";
  if (matchesPrefix(path, MAJOR_ROUTE_PREFIXES)) return "major";
  if (req.from && matchesPrefix(req.from, PLAY_ROUTE_PREFIXES)) return "major";
  return "minor";
}

function matchesPrefix(path: string, prefixes: readonly string[]): boolean {
  const clean = path.split("?")[0] ?? path;
  return prefixes.some((p) => clean === p || clean.startsWith(`${p}/`));
}

/** The style a kind uses, honouring a `?transition=` override. */
export function styleFor(kind: TransitionKind): TransitionStyle {
  return overriddenStyle() ?? (kind === "major" ? "deal" : "slide");
}

/* ---- URL overrides, read once ------------------------------------------- */

/**
 * Read from the whole href rather than `location.search`, because HashRouter
 * puts the app's own query string after the "#" — the same reason `perfHud` and
 * `shouldForceLongPolling` do it. Read once at module load, so a flag survives
 * the navigations that rewrite the hash.
 */
const href = typeof window === "undefined" ? "" : window.location.href;

function overriddenMajorMs(): number | null {
  const m = /[?&]tms=(\d{2,5})/.exec(href);
  if (!m) return null;
  return Number(m[1]);
}

function overriddenStyle(): TransitionStyle | null {
  const m = /[?&]transition=(slide|deal|spread|cut|fade|none)/.exec(href);
  if (!m) return null;
  return m[1] === "none" ? "fade" : (m[1] as TransitionStyle);
}

/** `?transition=none` means "get out of the way", not "cross-fade for 1.5s". */
export const transitionsDisabled = /[?&]transition=none/.test(href);

/**
 * `?tcard=150` — how tall a card is drawn, in CSS pixels, overriding the bounds
 * in `layout.ts`.
 *
 * The companion to `tms`, and it exists for the same reason: the first version
 * of this shipped cards two and a half times bigger on a desktop than on a
 * phone, which nobody spots in a unit test and everybody spots in a browser.
 * Null when unset.
 */
export const overriddenCardHeight: number | null = (() => {
  const m = /[?&]tcard=(\d{2,4})/.exec(href);
  return m ? Number(m[1]) : null;
})();
